/**
 * Home is the saloon.
 *
 * ============================== WHAT CHANGED, AND WHY ==========================
 * This was a landing page: a display line, two buttons, four sections of copy about
 * what the product is and what it will not claim. All of that still exists, word for
 * word, on `/about` (`routes/Landing.tsx`) — nothing was cut. But the master plan §4 is
 * unambiguous about what a player should meet first: "Home is the saloon: three doors,
 * your name, your Bucks, a chalkboard with today's Daily Spot", and Basics is a door
 * here as well as the first-run flow.
 *
 * That is what this is. One screen. Three doors, the Basics door, the chalkboard, and a
 * strip along the bottom carrying everything the site nav used to — because the nav is
 * not rendered on this screen (see `App.tsx`), and a door that leads nowhere back is
 * not a door.
 *
 * ## The backdrop is a PLATE, and it is a prop
 *
 * Exactly as the table takes its felt: a URL in, a picture on the screen. The default
 * is the saloon interior `sync-art.mjs` now ships (`/art/scenes/saloon-02-1000.webp`).
 * A different room, a seasonal plate, or a per-room backdrop later is a prop and not a
 * rebuild — and if the image is missing the scrim and the lamp glow still carry the
 * screen, because they are drawn in CSS from the kit's own tokens.
 *
 * ## Signed out sees the same saloon
 *
 * The doors open for everyone. The rooms gate at the SEAT, not at the door — a visitor
 * can walk in, look at the cards, read the Learn outline and see what the place is
 * before being asked for an account, which is the one thing the old landing page could
 * not do.
 * ===============================================================================
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { AuthArea } from "../components/AuthArea";
import { useMeContext } from "../lib/MeContext";
import { WalletGauge, useWallet } from "../lib/WalletContext";
import { Barkeep } from "../rooms/Barkeep";
import { ROOMS } from "../rooms/rooms";
import { personaAvatar, personaName } from "../settings/PersonaPicker";
import { getPersona, usePrefsStore } from "../settings/prefs";
import "../rooms/rooms.css";

/** The plate `sync-art.mjs` ships. A prop, so the next one is a prop change. */
const DEFAULT_PLATE = "/art/scenes/saloon-02-1000.webp";

/**
 * It was a PLACEHOLDER, and now it is not.
 *
 * Phase 2b passed the number in from outside so that wiring the real balance would be
 * deleting a default rather than finding a literal — which is exactly what happened
 * here. The export survives because the harness passes it to render the saloon without
 * a wallet provider, and because 5,000 is still a true statement about a new account.
 */
export const PLACEHOLDER_BUCKS = 5000;

export function Home({ plate = DEFAULT_PLATE }: { plate?: string }) {
  const { me } = useMeContext();
  const { canStake } = useWallet();
  const [barkeep, setBarkeep] = useState(false);
  /* Subscribed so that picking a face in the Basics flow shows up here immediately. */
  usePrefsStore();
  const slug = getPersona();
  const face = personaAvatar(128);
  const name = me?.user.email?.split("@")[0] ?? (slug ? personaName(slug) : "Stranger");

  return (
    <div className="saloon">
      <div className="saloon-plate" style={{ backgroundImage: `url(${plate})` }} aria-hidden="true" />
      <div className="saloon-vignette" aria-hidden="true" />

      <header className="saloon-head">
        <div className="saloon-wm">
          <b>BluffBusters</b>
          <small>The saloon</small>
        </div>

        <AuthArea placeholder={<span className="saloon-bucks" aria-hidden="true">&nbsp;</span>}>
          <SignedIn>
            <Link className="saloon-me" to="/account">
              {face
                ? <img src={face} alt="" />
                : <svg viewBox="0 0 40 40" aria-hidden="true">
                    <circle cx="20" cy="15" r="7.2" />
                    <path d="M5.5 40c0-8.2 6.3-13.2 14.5-13.2S34.5 31.8 34.5 40z" />
                  </svg>}
              <b>{name}</b>
            </Link>
            {/*
              * The gauge: balance, and what this visit has done to it.
              *
              * Phase 2b showed a bare number from a prop and kept the session half
              * hidden, because a session figure with no session behind it is worse than
              * no figure. Both halves are real now, and this is the same element.
              */}
            <WalletGauge className="saloon-bucks" onStake={() => setBarkeep(true)} />
          </SignedIn>
          <SignedOut>
            <Link className="saloon-me" to="/sign-in">
              <svg viewBox="0 0 40 40" aria-hidden="true">
                <circle cx="20" cy="15" r="7.2" />
                <path d="M5.5 40c0-8.2 6.3-13.2 14.5-13.2S34.5 31.8 34.5 40z" />
              </svg>
              <b>Sign in</b>
            </Link>
          </SignedOut>
        </AuthArea>
      </header>

      <div className="saloon-body">
        <nav className="doors" aria-label="Rooms">
          {ROOMS.map((r) => (
            <Link key={r.id} className={"door" + (r.open ? "" : " door-shut")}
                  to={r.open ? `${r.path}/play` : r.path}
                  style={{ "--room-accent": `var(${r.accent})` } as React.CSSProperties}>
              <b>{r.name}</b>
              <p>{r.blurb}</p>
              <em>{r.open ? "Open" : "Coming"}</em>
            </Link>
          ))}
        </nav>

        <div className="saloon-shelf">
          {/*
            * The chalkboard: the saloon's one marketing surface.
            *
            * Master plan §7 — one line, one tap, no countdown, no scarcity, nothing
            * third-party. The Daily Spot is Compete's, and Compete is a placeholder
            * this phase, so the board carries a static line and says where it goes.
            */}
          <Link className="chalkboard" to="/poker/compete">
            <small>Today's Daily Spot</small>
            <b>Nine-handed, 12 big blinds, the cutoff opens and it folds to you.</b>
            <p>One decision, the same one for everybody, until midnight.</p>
          </Link>

          <Link className="basics" to="/basics">
            <b>How to play</b>
            <p>
              What the screen is telling you, what the numbers mean, and a face to sit
              behind. Five minutes.
            </p>
          </Link>
        </div>
      </div>

      {/* A signed-out visitor has a device wallet and the same Barkeep; the doors open
          for everyone and the gate is at the seat. */}
      {barkeep && <Barkeep onClose={() => setBarkeep(false)} />}
      {!barkeep && canStake && (
        <button className="saloon-broke" type="button" onClick={() => setBarkeep(true)}>
          You're low on Bucks. The Barkeep will stake you.
        </button>
      )}

      <nav className="saloon-foot" aria-label="The rest of the site">
        <Link to="/about">About</Link>
        <Link to="/pricing">Pricing</Link>
        <SignedIn>
          <Link to="/report">Leak report</Link>
          <Link to="/account">Account</Link>
        </SignedIn>
        <Link to="/fair">The shuffle</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/refunds">Refunds</Link>
        <Link to="/responsible-play">Responsible play</Link>
      </nav>
    </div>
  );
}
